import { Info } from 'lucide-react'
import { expiryCode, type Bound } from '@apna/shared'
import { cn } from '@/lib/utils'
import { PayoffChart } from '@/components/payoff/PayoffChart'
import { compactInr } from '@/screens/terminal/quantity'
import type { BasketPreview } from '../hooks/useBasketPreview'

/**
 * What the basket costs, pays and risks — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CURVE SAYS WHICH DAY IT IS FOR — ROADMAP 9.67
 *
 * For a single-expiry basket that is "at expiry" and needs no saying. For a
 * calendar it is the NEAREST expiry, with the later legs still alive and
 * revalued at today's volatility — so the footer names the date, because
 * "max profit ₹8L" means nothing without "on 9 NOV, if vol is unchanged".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EACH FIGURE IS REFUSED ON ITS OWN
 *
 * A basket can be in a state where one of these is knowable and another is
 * not, and blanking all four because one input is missing hides answers we
 * have. So each cell says either a number or the sentence explaining its own
 * absence — never a zero, and never a dash with no reason beside it.
 *
 * The three refusals a trader will actually meet:
 *
 *   "no bid on 24,600 CE"   `netPremiumOf`'s own words. A quiet far strike
 *                           has no touch between trades, and a basket cannot
 *                           be priced through a missing one.
 *   "already expired"       a draft held over a settlement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AN UNBOUNDED TAIL IS A WORD, NOT A NUMBER
 *
 * `payoffShape` reports a naked short's loss as UNBOUNDED because spot has no
 * ceiling. Printing the loss at the right edge of the chart's window instead
 * would be a figure that moves when the window does — and it is exactly the
 * kind of number someone sizes a position against.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** A bound as a person reads it: a rupee figure, or the word for no limit. */
function boundText(b: Bound | undefined): string {
  if (!b) return '—'
  return b.kind === 'UNBOUNDED' ? 'Unlimited' : `₹${compactInr(Math.abs(b.value))}`
}

function Figure({
  label,
  value,
  why,
  tone,
  testId,
}: {
  label: string
  value: string | null
  /** Shown in place of the value. The reason, never a bare dash. */
  why?: string | null
  tone?: 'good' | 'bad'
  testId: string
}) {
  return (
    <div className="bb-fig" data-testid={testId}>
      <span className="bb-fig-k">{label}</span>
      {value !== null ? (
        <span
          className={cn(
            'bb-fig-v num',
            tone === 'good' && 'text-success',
            tone === 'bad' && 'text-destructive',
          )}
        >
          {value}
        </span>
      ) : (
        <span className="bb-fig-why">{why ?? '—'}</span>
      )}
    </div>
  )
}

export interface BasketSummaryProps {
  preview: BasketPreview
  spot: number
}

export function BasketSummary({ preview, spot }: BasketSummaryProps) {
  const { netPremium, premiumWhy, margin, marginWhy, shape, shapeWhy, payoffLegs } = preview
  const calendar = preview.expiry === null && preview.horizon !== null
  const on = preview.horizon ? (expiryCode(preview.horizon) ?? preview.horizon) : null

  return (
    <div className="bb-summary">
      <div className="bb-figs">
        <Figure
          label="Margin required ≈"
          value={margin?.ok ? `₹${compactInr(margin.margin.total)}` : null}
          why={marginWhy ?? 'Waiting for prices…'}
          testId="basket-margin"
        />
        <Figure
          label={netPremium !== null && netPremium < 0 ? 'Net debit' : 'Net credit'}
          value={netPremium !== null ? `₹${compactInr(Math.abs(netPremium))}` : null}
          why={premiumWhy}
          tone={netPremium !== null && netPremium >= 0 ? 'good' : 'bad'}
          testId="basket-premium"
        />
        <Figure
          label="Max profit"
          value={shape ? boundText(shape.maxProfit) : null}
          why={shapeWhy ?? 'Waiting for prices…'}
          tone="good"
          testId="basket-maxprofit"
        />
        <Figure
          label="Max loss"
          value={shape ? boundText(shape.maxLoss) : null}
          why={shapeWhy ?? 'Waiting for prices…'}
          tone="bad"
          testId="basket-maxloss"
        />
      </div>

      {shape && shape.breakevens.length > 0 && (
        <p className="bb-be num" data-testid="basket-breakevens">
          Breaks even at{' '}
          {shape.breakevens.map((b) => Math.round(b).toLocaleString('en-IN')).join(' and ')}
        </p>
      )}

      {payoffLegs && (
        <PayoffChart
          legs={payoffLegs}
          spot={spot > 0 ? spot : null}
          height={168}
          compact
          className="bb-chart"
          {...(preview.model && shape
            ? { model: { valuation: preview.model, shape } }
            : {})}
        />
      )}

      {/*
        WHAT THE CURVE IS, when it is not the plain one. A calendar's chart
        is a valuation on a date, not an arithmetic settlement, and the
        difference is the whole reason it can be drawn at all.
      */}
      {calendar && payoffLegs && (
        <p className="bb-note" data-testid="basket-horizon-note">
          <Info size={12} className="shrink-0" />
          <span>
            Drawn for <b>{on}</b>, the first expiry in this basket. Legs expiring later
            are revalued at today&rsquo;s volatility.
            {margin?.ok && preview.marginExpiries > 1 && (
              <>
                {' '}
                Margin is {preview.marginExpiries} scans added up — the exchange nets a
                calendar spread, so it will block less than this.
              </>
            )}
          </span>
        </p>
      )}
    </div>
  )
}
